import { useMemo } from 'react'

import { createColDefs } from '@fortem-monorepo/react-ag'
import { getAllGridData } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { Instruments } from '@fortem-monorepo/web-sdk'

import { barrier_type } from '@/ag-grid/columns/barrier-type/col-def'
import { contract_type } from '@/ag-grid/columns/contract-type/col-def'
import { InstrumentType } from '@/pages/instruments/new-instrument/constants/instruments'
import { ScheduleLeg } from '@/pages/instruments/new-instrument/schedule-builders/types'
import { useInstrumentsByIds } from '@/react-query/queries/instruments'

// TODO: hide columns and take data from form for payoff_barrier_level, knockdown_input_level:
// autocallbarrierlevelpct= 100,000 for vanilla spread
// - digital
// - vanilla spread
// - vanilla structured
// - range accrual
// - supertracker

const getScheduleColDefs = ({
  instrumentsMap,
  instrument_type,
}: {
  instrumentsMap: Map<number, Instruments.Instrument>
  instrument_type: InstrumentType
}) => {
  const instrumentTypesToHide: InstrumentType[] = [
    'digital',
    'vanilla-spread',
    'vanilla-structured',
    'range-accrual',
    'supertracker',
    'vanilla-digital',
  ]

  const hideCols = instrumentTypesToHide.includes(instrument_type)

  return createColDefs<ScheduleLeg>([
    {
      field: 'leg_id',
      headerName: 'Leg ID',
    },
    {
      field: 'option_type',
      rowGroup: true,
      hide: true,
      headerName: 'Option Type',
    },
    {
      field: 'instrument_id',
      headerName: 'Instrument ID',
      rowGroup: true,
      hide: true,
      valueFormatter: (params) => {
        const instrument = instrumentsMap.get(params.value)
        return instrument?.unique_identifier ?? params.value
      },
    },
    // @ts-expect-error
    {
      ...contract_type,
      editable: true,
    },
    // @ts-expect-error
    {
      ...barrier_type,
      editable: true,
    },
    {
      field: 'region_type',
      headerName: 'Region Type',
      editable: true,
    },
    {
      field: 'participation_rate',
      headerName: 'Participation Rate',
      editable: true,
    },
    {
      field: 'coupon_rate',
      headerName: 'Coupon Rate',
      editable: true,
    },
    {
      field: 'strike',
      headerName: 'Strike',
      editable: true,
    },
    {
      field: 'strike_percent',
      headerName: 'Strike Percent',
      editable: true,
    },
    {
      field: 'start_observation_date',
      headerName: 'Start Observation Date',
      editable: true,
    },
    {
      field: 'pay_date',
      headerName: 'Pay Date',
      editable: true,
    },
    {
      field: 'payoff_barrier_level',
      headerName: 'Payoff Barrier Level',
      editable: true,
      hide: hideCols,
    },
    {
      field: 'autocall_barrier_level',
      headerName: 'Autocall Barrier Level',
      editable: true,
      hide: hideCols,
    },
  ])
}

export interface ScheduleTableProps {
  instrument_type: InstrumentType
  onChangeData?: (schedule: ScheduleLeg[]) => void
  schedule: ScheduleLeg[]
}

export const ScheduleTable: React.FC<ScheduleTableProps> = ({
  instrument_type,
  schedule,
  onChangeData,
}) => {
  const instrumentIds = useMemo(() => {
    return schedule
      .filter((u) => u !== undefined)
      .map((s) => s.instrument_id)
      .filter((u) => u !== undefined)
      .sort()
  }, [schedule])

  const instrumentQuery = useInstrumentsByIds(instrumentIds)
  const instruments = instrumentQuery.data ?? []

  const instrumentsMap = useMemo(() => {
    const map: Map<number, Instruments.Instrument> = new Map()
    for (const instrument of instruments) {
      map.set(instrument.instrument_id, instrument)
    }
    return map
  }, [instruments])

  const colDefs = useMemo(
    () =>
      getScheduleColDefs({ instrumentsMap, instrument_type }).filter((col) => {
        // @ts-expect-error
        const field = col!.field as keyof ScheduleLeg
        return schedule.some((leg) => leg[field])
      }),
    [instrumentsMap, schedule],
  )

  if (schedule.length > 0 && !instrumentQuery.isFetching) {
    return (
      <Widget>
        <WidgetHeaderWithQuery title={'Generated Schedule'} />
        <WidgetContent>
          <CustomAgGrid
            rowData={schedule}
            columnDefs={colDefs}
            groupDefaultExpanded={-1}
            onCellValueChanged={(event: any) => {
              const data = getAllGridData<ScheduleLeg>(event.api)
              onChangeData?.(data)
            }}
          />
        </WidgetContent>
      </Widget>
    )
  }

  return null
}
